import React, { createContext, useState, useEffect, useCallback } from 'react';
import { groupApi } from '../services/groupApi';
import { useAuth } from '../hooks/useAuth';

export const AdminContext = createContext(null);

export function AdminProvider({ children }) {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasDashboard, setHasDashboard] = useState(false);
  const { user, isAuthenticated } = useAuth();

  const checkPermissions = useCallback(async () => {
    // Skip if not authenticated
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    try {
      // Check if user is admin first
      const isAdmin = user?.is_admin === true;

      if (isAdmin) {
        // Admin users get all permissions
        setPermissions(['dashboard', 'analytics', 'view_logs', 'view_stats', 'manage_users', 'manage_groups']);
        setHasDashboard(true);
        setLoading(false);
        return;
      }

      const response = await groupApi.getMyGroups();
      if (response.success && response.data) {
        // Get all permissions from user's groups
        const allPermissions = new Set();
        response.data.forEach(group => {
          if (group.permissions) {
            group.permissions.forEach(p => allPermissions.add(p));
          }
          // Group admins have all permissions
          group.members?.forEach(m => {
            if (m.role === 'admin') {
              allPermissions.add('dashboard');
              allPermissions.add('analytics');
              allPermissions.add('view_logs');
              allPermissions.add('view_stats');
              allPermissions.add('manage_users');
              allPermissions.add('manage_groups');
            }
          });
        });
        setPermissions(Array.from(allPermissions));
        setHasDashboard(allPermissions.has('dashboard'));
      }
    } catch (error) {
      console.error('Failed to load permissions:', error);
    } finally {
      setLoading(false);
    }
  }, [user, isAuthenticated]);

  useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);

  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  return (
    <AdminContext.Provider value={{ permissions, hasDashboard, hasPermission, loading, checkPermissions }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = React.useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}
